import * as React from "react";
import { Badge } from "react-bootstrap";
import apiClient from "../api";
import { gql } from "@apollo/client";
import { gettext } from "i18n";
import { timestampToDatetimeString } from "utils/formatting";

/**
 * User management related constants
 */

export const USER_ROLE_MEMBER = "member";
export const USER_ROLE_CONTROLLER = "quality_controller";
export const USER_ROLE_ADMIN = "admin";

export const ORG_ACTION_ADD = "add";
export const ORG_ACTION_EDIT = "edit";
export const ORG_ACTION_REMOVE = "remove";

export const USER_ROLE_OPTIONS: Record<string, string> = {
  [USER_ROLE_MEMBER]: "Member",
  [USER_ROLE_CONTROLLER]: "Quality Controller",
  [USER_ROLE_ADMIN]: "Admin",
};

/**
 * User management utilities
 */

/**
 * Name to show for whoever created a record. Records outlive their creator, so
 * the user is missing once the account has been deleted.
 */
export const getCreatorDisplayName = (
  user: { display_name?: string | null } | string | null | undefined,
): string => {
  const name = typeof user === "string" ? user : user?.display_name;
  return name || gettext("Deleted user");
};

export interface ManagedOrganizationOption {
  id: number;
  name: string;
  role: string;
}

export const loadManagedOrganizations = async ({
  setManagedOrganizations,
}: {
  setManagedOrganizations: React.Dispatch<
    React.SetStateAction<ManagedOrganizationOption[]>
  >;
}) => {
  try {
    const result = await apiClient.request("/auth/managed-organizations", {
      method: "GET",
    });
    setManagedOrganizations(result as ManagedOrganizationOption[]);
  } catch (e) {
    console.error(e);
  }
};

// The rules mg-core applies to every new password (validate_new_password)
export const PASSWORD_RULES_HINT =
  "At least 12 characters, including a lowercase letter, an uppercase letter and a special character.";

export interface EditableUserOption {
  id: number;
  email: string;
  display_name: string;
  is_admin: boolean;
  can_add_organizations: number[];
  locale: string;
  can_edit_formulas: boolean;
  can_edit_station_parameters: boolean;
  // Of the organizations the requesting user manages (all for a global admin)
  organizations?: { id: number; name: string; role: string }[];
  // Unknown (null) for accounts created before December 2022
  created_at?: string | null;
  has_password?: boolean;
  password_changed_at?: string | null;
  // Set while the password is one the user did not choose (generated, or set by an admin)
  password_assigned_at?: string | null;
  password_change_requested_at?: string | null;
  // Confirmed second factors (totp, passkey, email)
  mfa_enabled?: boolean;
  mfa_methods?: string[];
  // Whether MFA is mandatory for the user, and why: global_admin, user (a
  // global admin required it) or organization
  mfa_required?: boolean;
  mfa_required_reasons?: string[];
  mfa_required_by_admin?: boolean;
}

export type MfaState = "enabled" | "missing" | "off";

export const MFA_STATE_LABELS: Record<MfaState, string> = {
  enabled: "MFA on",
  missing: "MFA required, not set up",
  off: "MFA off",
};

export const getMfaState = (user: EditableUserOption): MfaState => {
  if (user.mfa_enabled) {
    return "enabled";
  }
  return user.mfa_required ? "missing" : "off";
};

const MFA_METHOD_SHORT_LABELS: Record<string, string> = {
  totp: "App",
  passkey: "Passkey",
  email: "Email",
};

const MFA_REASON_LABELS: Record<string, string> = {
  global_admin: "Global admin",
  user: "Required by an administrator",
  organization: "Required by an organization",
};

/** Whether the user has MFA, how, and whether it is mandatory for them */
export const MfaStateBadge: React.FC<{ user: EditableUserOption }> = ({
  user,
}) => {
  const state = getMfaState(user);
  const reasons = (user.mfa_required_reasons ?? [])
    .map((reason) => gettext(MFA_REASON_LABELS[reason] ?? reason))
    .join(", ");
  return (
    <span title={reasons ? gettext("Required: %1", reasons) : undefined}>
      <Badge
        bg={
          state === "enabled"
            ? "success"
            : state === "missing"
              ? "danger"
              : "secondary"
        }
      >
        {gettext(MFA_STATE_LABELS[state])}
      </Badge>
      {state === "enabled" && (
        <small className="text-muted ms-2">
          {(user.mfa_methods ?? [])
            .map((method) => gettext(MFA_METHOD_SHORT_LABELS[method] ?? method))
            .join(", ")}
        </small>
      )}
      {user.mfa_required && state === "enabled" && (
        <Badge bg="light" text="dark" className="ms-2">
          {gettext("Required")}
        </Badge>
      )}
    </span>
  );
};

export type PasswordState =
  "requested" | "notSet" | "assigned" | "changed" | "unknown";

export const PASSWORD_STATE_LABELS: Record<PasswordState, string> = {
  requested: "Change requested",
  notSet: "Not set",
  assigned: "Not changed by user",
  changed: "Changed by user",
  unknown: "Unknown",
};

export const PASSWORD_STATE_BADGES: Record<PasswordState, string> = {
  requested: "warning",
  notSet: "secondary",
  assigned: "danger",
  changed: "success",
  unknown: "light",
};

/**
 * The state of the user's password and since when. "unknown" is a password
 * last changed before changes were recorded (28 October 2026).
 */
export const getPasswordState = (
  user: EditableUserOption,
): { state: PasswordState; since?: string | null } => {
  if (user.password_change_requested_at) {
    return { state: "requested", since: user.password_change_requested_at };
  }
  if (!user.has_password) {
    return { state: "notSet" };
  }
  if (user.password_assigned_at) {
    return { state: "assigned", since: user.password_assigned_at };
  }
  if (user.password_changed_at) {
    return { state: "changed", since: user.password_changed_at };
  }
  return { state: "unknown" };
};

export const PasswordStateBadge: React.FC<{ user: EditableUserOption }> = ({
  user,
}) => {
  const { state, since } = getPasswordState(user);
  return (
    <>
      <Badge
        bg={PASSWORD_STATE_BADGES[state]}
        text={state === "unknown" ? "dark" : undefined}
      >
        {gettext(PASSWORD_STATE_LABELS[state])}
      </Badge>
      {since && (
        <small className="text-muted ms-2">
          {timestampToDatetimeString(since)}
        </small>
      )}
    </>
  );
};

export const requestPasswordChange = (userId: number) =>
  apiClient.request("/admin/request-password-change", {
    method: "POST",
    data: { user_id: userId },
  });

// Users shown on each page of User Management's list
export const EDITABLE_USERS_PER_PAGE = 50;

export interface EditableUsersQuery {
  // From 1
  page: number;
  perPage?: number;
  // Every word must be found in the name, email or an organization's name
  search?: string;
  passwordState?: PasswordState | "";
  mfaState?: MfaState | "";
}

export interface EditableUsersPage {
  users: EditableUserOption[];
  // Of all the pages
  total: number;
  page: number;
  per_page: number;
}

/** The path of a page of users, with only the filters in use */
export const editableUsersPath = ({
  page,
  perPage = EDITABLE_USERS_PER_PAGE,
  search,
  passwordState,
  mfaState,
}: EditableUsersQuery): string => {
  const params = new URLSearchParams({
    page: String(page),
    per_page: String(perPage),
  });
  if (search?.trim()) {
    params.set("search", search.trim());
  }
  if (passwordState) {
    params.set("password_state", passwordState);
  }
  if (mfaState) {
    params.set("mfa_state", mfaState);
  }
  return `/auth/editable-users?${params}`;
};

/** A page of the users the current user may edit, searched and filtered by the server */
export const loadEditableUsersPage = async (
  query: EditableUsersQuery,
): Promise<EditableUsersPage> =>
  (await apiClient.request(editableUsersPath(query), {
    method: "GET",
  })) as EditableUsersPage;

/** Enough of a user to choose them from a list */
export interface UserChoice {
  id: number;
  email: string;
  display_name: string;
}

/** Every user the current user may edit, ordered by email, for choosing one */
export const loadUserChoices = async (): Promise<UserChoice[]> =>
  (await apiClient.request("/auth/editable-users?basic=1", {
    method: "GET",
  })) as UserChoice[];

export const loadEditableUser = async ({ userId }: { userId: number }) => {
  try {
    const users = await apiClient.request(
      `/auth/editable-users?user_id=${userId}`,
      { method: "GET" },
    );
    return (users as EditableUserOption[])?.[0];
  } catch (e) {
    console.error(e);
  }
  return undefined;
};

export const loadContextPermissions = async ({
  category,
  target,
  requiredPermission,
}: {
  category: string;
  target?: string;
  requiredPermission?: string;
}): Promise<string[]> => {
  let result;
  try {
    result = await apiClient.request(
      "/auth/context-permissions",
      {
        method: "POST",
        data: {
          category,
          target,
          requiredPermission,
        },
      },
      false,
    );
  } catch (e) {
    console.error(e);
  }
  return result?.permissions;
};

/**
 * User Organizations management
 */

export const GET_ORGANIZATIONS = gql`
  query GetOrganizations {
    organization(order_by: { id: asc }) {
      id
      name
      slug
    }
  }
`;

export const GET_ORGANIZATION = gql`
  query GetOrganization($organizationId: Int) {
    organization(where: { id: { _eq: $organizationId } }) {
      id
      name
      slug
    }
  }
`;

export const GET_USER_ORGANIZATIONS = gql`
  query GetUserOrganizations($userId: Int) {
    organization_user(where: { user_id: { _eq: $userId } }) {
      role
      organization {
        id
        name
        slug
      }
    }
  }
`;

export const GET_ORGANIZATION_MAPS = gql`
  query GetOrganizationMaps {
    organization_map(
      order_by: [{ organization: { name: asc } }, { name: asc }]
    ) {
      id
      name
      active
      organization_id
      organization {
        id
        name
        slug
      }
      organization_map_stations {
        id
        station_id
        station {
          id
          identifier
          name
          lat
          lon
        }
      }
    }
  }
`;

export const GET_ORGANIZATION_MAP = gql`
  query GetOrganizationMap($id: Int!) {
    organization_map(where: { id: { _eq: $id } }) {
      id
      name
      organization {
        id
        name
      }
    }
  }
`;
